/**
 * FMO / TrackNet debug for the review HUD.
 * Prefers pipeline `result.debug`; otherwise derives a report from samples.
 */

function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function clusterDetectedSamples(samples, maxGapMs = 80) {
  const hits = (Array.isArray(samples) ? samples : [])
    .filter((s) => s?.detected)
    .map((s) => ({
      t_ms: num(s.t_ms),
      frame: num(s.frame),
    }))
    .sort((a, b) => a.t_ms - b.t_ms);
  if (!hits.length) return [];
  const clusters = [];
  let start = hits[0];
  let prev = hits[0];
  let count = 1;
  for (let i = 1; i < hits.length; i += 1) {
    const cur = hits[i];
    if (cur.t_ms - prev.t_ms <= maxGapMs) {
      count += 1;
      prev = cur;
      continue;
    }
    clusters.push({
      t0_ms: start.t_ms,
      t1_ms: prev.t_ms,
      n: count,
    });
    start = cur;
    prev = cur;
    count = 1;
  }
  clusters.push({ t0_ms: start.t_ms, t1_ms: prev.t_ms, n: count });
  return clusters;
}

function windowCoverageMs(windows) {
  if (!Array.isArray(windows) || !windows.length) return 0;
  return windows.reduce((sum, w) => {
    const t0 = num(w?.t0 ?? w?.start_ms, -1);
    const t1 = num(w?.t1 ?? w?.end_ms, -1);
    return t1 > t0 && t0 >= 0 ? sum + (t1 - t0) : sum;
  }, 0);
}

function deriveLikelyCauses(report) {
  const causes = [];
  const nDet = num(report.n_detected);
  const nFrames = num(report.n_frames);
  const fps = num(report.video_fps);
  const rate = nFrames > 0 ? nDet / nFrames : 0;
  const nWin = num(report.n_stroke_windows);
  const cover = num(report.window_coverage_ms);
  const durationMs = nFrames > 0 && fps > 0 ? (nFrames / fps) * 1000 : 0;
  const coverFrac = durationMs > 0 ? cover / durationMs : 0;
  const nClusters = num(report.n_clusters);
  const longest = num(report.longest_cluster_n);

  if (nDet === 0 && nFrames > 0) {
    causes.push(
      "Zero kept detections. Overlay will show no ball. Regen ball after this build to see the native FMO funnel (raw vs keep-box vs smooth vs windows)."
    );
  } else if (rate > 0 && rate < 0.02) {
    causes.push(
      `Only ${nDet} kept frames (${Math.round(rate * 1000) / 10}% of ${nFrames}). The overlay looks empty except a few flashes.`
    );
  }
  if (fps >= 90 && rate < 0.05) {
    causes.push(
      `Video is ${Math.round(fps)} fps. FMO is a streak detector and under-fires when the ball is a sharp disc (little motion blur). TrackNet usually keeps ~20% of frames on the same exercise.`
    );
  }
  if (nClusters > 0 && longest > 0 && longest <= 6 && nDet > 0) {
    causes.push(
      `${nClusters} isolated bursts (longest ${longest} frames). Typical of FMO catching a streak endpoint, not a continuous track.`
    );
  }
  if (nWin > 0 && coverFrac >= 0.5) {
    causes.push(
      `Stroke gating is on (${nWin} windows, ~${Math.round(coverFrac * 100)}% of the clip). Windows cover most of the video, so they are probably not the main wipe.`
    );
  } else if (nWin > 0 && coverFrac < 0.25 && nDet > 0 && rate < 0.02) {
    causes.push(
      `Stroke gating is on (${nWin} windows, only ~${Math.round(coverFrac * 100)}% of the clip). Hits outside those windows are dropped.`
    );
  }
  return causes.slice(0, 5);
}

/**
 * @param {object|null} ballResult
 * @returns {object|null}
 */
export function resolveBallDebug(ballResult) {
  if (!ballResult || typeof ballResult !== "object") return null;
  const pipeline = ballResult.debug;
  if (pipeline && typeof pipeline === "object" && pipeline.source === "pipeline") {
    return pipeline;
  }

  const settings = ballResult.settings || {};
  const summary = ballResult.summary || {};
  const samples = Array.isArray(ballResult.samples) ? ballResult.samples : [];
  const nFrames = num(summary.n_frames, samples.length);
  const nDetected = num(
    summary.n_detected,
    samples.filter((s) => s?.detected).length
  );
  if (nFrames <= 0 && nDetected <= 0) return null;

  const clusters = clusterDetectedSamples(samples);
  const windows = settings.stroke_windows_ms;
  const report = {
    detector: settings.detector || "fmo",
    source: "derived",
    video_fps: num(settings.fps),
    ball_detection_fps: num(settings.ball_detection_fps, num(settings.fps)),
    n_frames: nFrames,
    n_detected: nDetected,
    detect_rate:
      nFrames > 0 ? Math.round((nDetected / nFrames) * 10000) / 10000 : 0,
    n_stroke_windows: Array.isArray(windows) ? windows.length : 0,
    window_coverage_ms: windowCoverageMs(windows),
    n_clusters: clusters.length,
    longest_cluster_n: clusters.reduce((m, c) => Math.max(m, num(c.n)), 0),
    clusters: clusters.slice(0, 16),
  };
  report.likely_causes = deriveLikelyCauses(report);
  return report;
}
